import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { ProfileFormValues, profileSchema } from "./schema";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { iUser } from "@/@sdk/user-management/user-management.models";
import { uploadFileWithValidation } from "../common/file-picker/upload-with-validation";
import { SDK } from "@/@sdk";
import { AvatarUploadValidations } from "../common/file-picker/upload-validations";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Camera, Trash2, User } from "lucide-react";
import { useStore } from "react-redux";
import { reloadCurrentUser } from "@/store/modules/users/users.helpers";

interface ProfileFormProps {
  user: iUser;
  onBack?: () => void;
  onSaved?: () => void;
  mode?: "settings" | "wizard";
  prefill?: Partial<ProfileFormValues>;
}

export function ProfileForm({
  user,
  onBack,
  onSaved,
  mode = "settings",
  prefill,
}: ProfileFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const store = useStore();

  const { doAction: editCurrentUser } = useSDKActionWithDeps(
    () => ({
      action: (SDK) => (edits) => SDK.editCurrentUser(edits),
      failureMessage: "Something went wrong",
    }),
    []
  );

  const [isUploading, setIsUploading] = useState(false);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: prefill?.firstName || user?.data?.firstName || "",
      lastName: prefill?.lastName || user?.data?.lastName || "",
      avatar: prefill?.avatar || user?.data?.avatar || "",
      phoneNumber: prefill?.phoneNumber || user?.data?.phoneNumber || "",
      description: prefill?.description || user?.data?.description || "",
      bio: prefill?.bio || user?.data?.bio || "",
      availability_days:
        prefill?.availability_days || user?.data?.availability_days || "",
      availability_hours:
        prefill?.availability_hours || user?.data?.availability_hours || "",
    },
  });

  const uploadAvatar = useCallback(
    (file: File) => {
      setIsUploading(true);
      uploadFileWithValidation({
        file: file,
        reference: {
          type: "USER",
          entityId: user?.id,
        },
        uploadFileFunction: (file: File) => {
          return SDK.uploadFile(
            file,
            {
              type: "USER",
              entityId: user?.id,
            },
            []
          );
        },
        validations: AvatarUploadValidations,
        resizeBeforeUpload: {
          maxWidth: 512,
          maxHeight: 512,
          compressFormat: "JPEG",
        },
      })
        .then((file) => {
          form.setValue("avatar", file.url);
        })
        .catch((e) => {
          if (typeof e !== "string") {
            toast.error("Something went wrong while uploading");
          }
        })
        .finally(() => {
          setIsUploading(false);
        });
    },
    [user?.id, form]
  );

  const handleAvatarUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      console.log("e", e);
      const file = e.target.files?.[0];
      if (!file) return;

      uploadAvatar(file);
    },
    [uploadAvatar]
  );

  const handleSubmit = async (data: ProfileFormValues) => {
    setIsLoading(true);
    try {
      await editCurrentUser({ data });
      await reloadCurrentUser(store);
      if (mode === "wizard") {
        onSaved?.();
      } else {
        onBack?.();
        toast.success("Profile updated");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-4"
        id="profile-form"
      >
        <div className="flex flex-col items-center mb-6">
          <FormField
            control={form.control}
            name="avatar"
            render={({ field }) => (
              <FormItem className="relative">
                <div className="flex flex-col items-center">
                  <Avatar className="h-24 w-24">
                    <AvatarImage src={field.value} />
                    <AvatarFallback>
                      <User className="h-12 w-12 text-muted-foreground" />
                    </AvatarFallback>
                    {isUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                      <Loader2 className="h-8 w-8 animate-spin text-white" />
                    </div>
                  )}
                  </Avatar>
                 

                  <div className="mt-4 flex gap-2">
                    <div className="relative cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        id="avatar-upload-file"
                        disabled={isUploading}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isUploading}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          document
                            .getElementById("avatar-upload-file")
                            ?.click();
                        }}
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        Upload
                      </Button>
                    </div>
                    {field.value && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          form.setValue("avatar", "");
                        }}
                        disabled={isUploading}
                        type="button"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description/Role</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="availability_days"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Available Days</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g. Monday - Friday" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="availability_hours"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Available Hours</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g. 9:00 AM - 5:30 PM" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="bio"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Bio</FormLabel>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2 mt-6">
          {mode === "settings" ? (
            <>
              <Button variant="outline" onClick={onBack} type="button">
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </>
          ) : (
            <Button type="submit" disabled={isLoading} className="w-full sm:w-auto">
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save and continue
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
